import React, { useEffect, useState } from 'react'
import { FieldErrors, FormProvider, useForm, useWatch } from 'react-hook-form'

import { AppBar, Tab, Tabs } from '@material-ui/core'
import Swal from 'sweetalert2'

import Modal from '~/components/modal'
import { isSoja } from '~/enums/enum'
import { growCropsMapper } from '~/mappers/grow-crops-mapper'
import { resetFieldMapper } from '~/mappers/variedade-mapper'
import { useGrowCropsStore } from '~/store/variety/growcrops'
import {
  VarietyProps,
  VorietyTransterInputProps,
  VorietyTransterOutputProps,
} from '~/types/grow-crops-types'
import { apiFieldErrors } from '~/utils/api-field-errors'

import { CystNematodeForms } from '../cyst-nematoide-forms'
import GrowCropsForms from '../growcrops-forms'
import { IllnessForms } from '../illness-forms'
import { LesionNematodeForms } from '../lesion-nematoide-forms'
import { TablePanel } from '../tab-panel'
import { getEmptyFeatureFields } from './empty-feature-fields'
import { cultivarResolver } from './schema'
import { shouldAskNoInformation } from './should-ask-no-information'
import strings from './strings'

const CULTIVAR_TAB_FIELDS = [
  'cultura_id',
  'nome',
  'genetica_id',
  'tecnologia',
  'grupo_maturacao',
  'status',
  'sts',
]

interface Props {
  variety?: VarietyProps
}

export const TabsGrowCrops: React.FC<Props> = ({ variety }) => {
  const controllerModalTabs = useGrowCropsStore((state) => state.controllerModalTabs)
  const isLoading = useGrowCropsStore((state) => state.isLoading)
  const clearFormsCreateUser = useGrowCropsStore((state) => state.clearFormsCreateUser)
  const modalTabsController = useGrowCropsStore((state) => state.modalTabsController)
  const updateVarietyById = useGrowCropsStore((state) => state.updateVarietyById)
  const createVariety = useGrowCropsStore((state) => state.createVariety)

  const [tabsControl, setTabsControl] = useState<number>(0)
  const [hasVisitedFeatureTab, setHasVisitedFeatureTab] = useState<boolean>(false)

  const methods = useForm<any>({
    resolver: cultivarResolver,
    defaultValues: resetFieldMapper(variety),
  })

  const { reset } = methods

  const selectedCultureId = useWatch({ control: methods.control, name: 'cultura_id' })
  const isSojaSelected = isSoja(selectedCultureId)

  useEffect(() => {
    setTabsControl(0)
    setHasVisitedFeatureTab(false)
    clearFormsCreateUser()
    reset(resetFieldMapper(variety))
  }, [variety, reset, clearFormsCreateUser])

  const tabsHandleControl = (index: any) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    }
  }

  const handleTabsControl = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabsControl(newValue)

    if (newValue !== 0) {
      setHasVisitedFeatureTab(true)
    }
  }

  const closeModalTabs = () => {
    modalTabsController(false)
    clearFormsCreateUser()
  }

  // A store engole o erro do save (Promise<void>), então o único sinal de 422 é o
  // messageError. Os campos de `variedade` só existem na aba Cultivar, que desmonta
  // com o TablePanel — sem isto o destaque acende fora da tela.
  const focusTabWithApiError = () => {
    const { messageError } = useGrowCropsStore.getState()

    const hasCultivarFieldError = Object.keys(apiFieldErrors(messageError)).some(
      (field) => field === 'variedade' || CULTIVAR_TAB_FIELDS.includes(field),
    )

    if (hasCultivarFieldError) {
      setTabsControl(0)
    }
  }

  // `createEmptyVariety()` devolve id: 0 no cadastro, então `!variety?.id` — e não
  // uma checagem de undefined — é o teste de "é cadastro".
  const saveVariety = async (payload: VorietyTransterOutputProps) => {
    if (!variety?.id) {
      await createVariety(payload)
    } else {
      await updateVarietyById(payload, variety.id)
    }

    focusTabWithApiError()
  }

  const handleChangeFieldValue = async (
    data: VorietyTransterInputProps,
    emptyFeatureFields: string[],
  ) => {
    return Swal.fire({
      icon: 'warning',
      title: 'Atenção',
      text: 'Deseja preencher como "Sem Informação" as características que não estão preenchidas?',
      confirmButtonColor: '#2b78c0',
      showCancelButton: true,
      cancelButtonColor: '#ff7d89',
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não',
      allowOutsideClick: false,
      allowEscapeKey: false,
      customClass: {
        container: 'alert-container',
      },
    }).then(async (result: any) => {
      if (!result.isConfirmed && result.dismiss !== Swal.DismissReason.cancel) {
        return
      }

      const noInformationValues: Record<string, string> = {}

      if (result.isConfirmed) {
        emptyFeatureFields.forEach((field) => {
          methods.setValue(field, 'Sem Informação')
          noInformationValues[field] = 'Sem Informação'
        })
      }

      await saveVariety(growCropsMapper({ ...data, ...noInformationValues }))
    })
  }

  const onInvalid = (formErrors: FieldErrors) => {
    const hasCultivarTabError = Object.keys(formErrors).some((field) =>
      CULTIVAR_TAB_FIELDS.includes(field),
    )

    if (hasCultivarTabError) {
      setTabsControl(0)
    }
  }

  const onSubmit = async (data: VorietyTransterInputProps) => {
    const emptyFeatureFields = getEmptyFeatureFields(methods.getValues())

    if (
      shouldAskNoInformation({ emptyFeatureFields, hasVisitedFeatureTab, isSoja: isSojaSelected })
    ) {
      await handleChangeFieldValue(data, emptyFeatureFields)
      return
    }

    await saveVariety(growCropsMapper(data))
  }

  const submitForm = methods.handleSubmit(onSubmit, onInvalid)

  return (
    <Modal
      open={controllerModalTabs}
      onClose={closeModalTabs}
      title={strings.textModal.ModalTitle}
      onSave={submitForm}
      save
      scroll
      isLoad={isLoading}
      disableCloseClickOut
    >
      <FormProvider {...methods}>
        <form onSubmit={submitForm}>
          <AppBar position="static">
            {isSojaSelected ? (
              <Tabs value={tabsControl} onChange={handleTabsControl} centered>
                <Tab label="Cultivar" {...tabsHandleControl(0)} id="tabs-modal-cultivar" />
                <Tab label="Nematoides Cisto" {...tabsHandleControl(1)} id="tabs-modal-cultivar" />
                <Tab
                  label="Nematoides Lesão/Galha"
                  {...tabsHandleControl(2)}
                  id="tabs-modal-cultivar"
                />
                <Tab label="Doenças" {...tabsHandleControl(3)} id="tabs-modal-cultivar" />
              </Tabs>
            ) : (
              <Tabs value={tabsControl} onChange={handleTabsControl}>
                <Tab label="Cultivar" {...tabsHandleControl(0)} />
              </Tabs>
            )}
          </AppBar>
          <TablePanel value={tabsControl} index={0}>
            <GrowCropsForms isEditing={!!variety?.id} />
          </TablePanel>
          {isSojaSelected && (
            <>
              <TablePanel value={tabsControl} index={1}>
                <CystNematodeForms />
              </TablePanel>
              <TablePanel value={tabsControl} index={2}>
                <LesionNematodeForms />
              </TablePanel>
              <TablePanel value={tabsControl} index={3}>
                <IllnessForms />
              </TablePanel>
            </>
          )}
        </form>
      </FormProvider>
    </Modal>
  )
}
